<script setup>
import { computed, inject, ref, shallowRef, toValue, watch } from 'vue'
import { syncRef } from '@vueuse/core'
import SelectId from '@/components/select/selectIdObject.vue'
import Step from './step.vue'
import CommunicationForm from './communication/form.vue'
import ReplyUpload from './reply_upload.vue'
import { useApi } from '@provise/composables/api.js'

defineProps({
  edit: Boolean,
  editPermission: {
    type: Boolean,
    default: true,
  },
})
defineEmits(['save'])
const model = defineModel()
const reply = ref(null)
const complaint = inject('complaint', () => ref(null), true)
const commurl = computed(
  () => `complaints/${complaint.value.id}/communication/${reply.value.communication?.id}`
)
const [replies, fetchReplies] = inject('replies', () => [shallowRef([]), () => {}], true)
const repliesOptions = computed(() => {
  return pickData(replies.value)
})
const rcom = ref({})
syncRef(
  rcom,
  computed(() => {
    if (!reply.value) {
      return null
    }
    return reply.value?.communication || {}
  }),
  { direction: 'rtl' }
)
watch(
  () => complaint.value?.status?.key,
  k => {
    if (k == 'done') {
      reply.value = null
    }
  }
)

function pickData(dr) {
  dr = dr.map(r => {
    let optLabel = [r.name]
    if (r.uploads.length) {
      optLabel.push(r.uploads[0].filename)
      if (r.name) {
        optLabel.push(`<span class="text-muted small">(${r.name})</span>`)
      }
    }
    if (r.aligned === true) {
      optLabel.push('<span class="text-success">Согласовано</span>')
    }
    optLabel = optLabel.join(' ')
    return {
      ...r,
      optLabel,
    }
  })
  return dr.filter(
    r =>
      r.aligned === true &&
      (!complaint.value.need_commercial_approve ||
        (complaint.value.need_commercial_approve && r.commerce_aligned === true))
  )
}

const currUser = computed(() => {
  return model.value.sla_users?.delivery_answer || null
})

async function onSent() {
  await fetchReplies()
  reply.value = null
}

async function onReplyUploaded(reply) {
  const modelValue = toValue(model)
  await useApi({
    method: 'post',
    model: { approval_comment: '' },
  }).refresh({
    url: `complaints/${modelValue.id}/reply/${reply.id}/approve`,
  })
  await fetchReplies()
}
</script>
<template>
  <Step v-model="model" :edit="edit">
    <div class="mb-3">
      <ReplyUpload v-if="model.no_approve" v-model="model" @done="onReplyUploaded" />
    </div>

    <div class="card mb-4" v-if="reply">
      <div class="card-header d-flex">
        Сообщение
        <span class="badge text-bg-secondary ms-3" v-if="rcom?.sent">Отправлено</span>
        <button type="button" class="btn-close ms-auto" @click="reply = null" />
      </div>
      <div class="card-body">
        <label class="form-label">Выбранный документ</label>
        <SelectId :options="repliesOptions" values="optLabel" v-model="reply" disabled />
        <p></p>
        <CommunicationForm
          v-model="rcom"
          :url="commurl"
          :reply="reply"
          :complaint-id="complaint.id"
          @save="fetchReplies"
          @sent="onSent"
        />
      </div>
    </div>

    <div class="vstack mb-4" v-if="repliesOptions.length">
      <template v-for="rep of repliesOptions" :key="rep.id">
        <div class="gap-1">
          Ответ
          <a
            v-if="rep.uploads.length"
            :download="rep.uploads[0].filename"
            :href="`/api/v1.0/download/${rep.uploads[0].secure_filename}`"
            >{{ rep.name }} ({{ rep.uploads[0].filename }})</a
          >
          <template v-else>{{ rep.name }}</template>
          &puncsp;
          <template v-if="rep.communication">
            <template v-if="rep.communication?.sent">
              <span class="text-success">отправлен</span>
            </template>
            <template v-else>
              <span class="text-warning">ожидает отправки</span>
            </template>
            <template v-if="rep.communication?.channel.key == 'email'">
              <template v-if="rep.communication?.email_addresses.length">
                для&puncsp;
                <template v-for="email of rep.communication?.email_addresses || []" :key="email"
                  >{{ email.email }}
                </template>
              </template>
            </template>
            <template v-else-if="rep.communication?.address && rep.communication?.address.length">
              для {{ rep.communication.address }} </template
            >&puncsp;
            <a href="#" @click.prevent="reply = rep" v-if="editPermission">Перейти к форме</a>
          </template>
          <template v-else>
            <span class="text-danger">ожидает заполнения</span>&puncsp;
            <a href="#" @click.prevent="reply = rep" v-if="currUser && editPermission"
              >Заполнить форму</a
            >
          </template>
        </div>
      </template>
    </div>

    <div class="hstack gap-2 flex-wrap" v-if="!currUser && editPermission">
      <button type="button" class="btn btn-outline-primary" @click="$emit('save')">
        <x-icon icon="pencil" />
        Взять в работу
      </button>
    </div>
    <div v-if="currUser && editPermission">
      <div v-if="$slots.sla" class="my-4">
        <slot name="sla" />
      </div>
      <slot name="form-post-controls" />
    </div>

    <div v-if="!editPermission" class="alert alert-info">
      Редактирование невозможно. У вас нет прав
    </div>
  </Step>
</template>
<style></style>
